// The Casino: blackjack against the house. The server deals and decides;
// the dealer's face-down card is never sent to the browser until the hand ends.
import { useEffect, useRef, useState } from 'react';
import { play, sequence } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';
import { api } from '../api.ts';
import { useGame, money, fmt } from '../state.tsx';
import { Loading, Panel, go, useLoad, PlayerLink } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { SlotsMachine } from './Slots.tsx';
import { RouletteTable } from './Roulette.tsx';

const SUIT: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };

export function Card({ code, delay = 0 }: { code: string; delay?: number }) {
  // Remember if this card was face down a moment ago, so it flips over when revealed.
  const was = useRef(code);
  const flipped = was.current === '??' && code !== '??';
  useEffect(() => { was.current = code; }, [code]);
  const style = delay ? { animationDelay: `${delay}ms` } : undefined;
  if (code === '??') return <div className="card-back deal-in" style={style} title="Face down" />;
  const rank = code.slice(0, -1);
  const suit = code.slice(-1);
  const red = suit === 'H' || suit === 'D';
  return (
    <div className={`card-face ${red ? 'red' : 'black'} ${flipped ? 'flip-in' : 'deal-in'}`} style={flipped ? undefined : style} title={`${rank}${SUIT[suit]}`}>
      <div className="corner">{rank}<br />{SUIT[suit]}</div>
      <div className="pip">{SUIT[suit]}</div>
      <div className="corner br">{rank}<br />{SUIT[suit]}</div>
    </div>
  );
}

const OUTCOME: Record<string, [string, string]> = {
  blackjack: ['BLACKJACK! Pays 3 to 2', '#ffd700'],
  win: ['YOU WIN', '#8f6'],
  push: ['PUSH: your bet is returned', '#ddd'],
  lose: ['DEALER WINS', '#f66'],
  bust: ['BUST', '#f66'],
  dealer_blackjack: ['DEALER HAS BLACKJACK', '#f66'],
};

function BlackjackTable() {
  const { me, act, content } = useGame();
  const [table, setTable] = useState<any>(null);
  const [bet, setBet] = useState('');
  const [busy, setBusy] = useState(false);
  // How many cards were already on the table before the last move (new ones slide in one after another).
  const [base, setBase] = useState({ d: 0, p: 0 });
  const [hand, setHand] = useState(0);
  // The result banner waits until the last card has landed.
  const [showOutcome, setShowOutcome] = useState(true);

  useEffect(() => {
    api.get('/api/casino').then(setTable);
  }, []);
  useEffect(() => {
    if (table && !bet) setBet(String(Math.min(table.maxBet, Math.max(table.minBet, 1000))));
  }, [table]);

  if (!table || !me) return <Loading />;
  const send = async (path: string, body?: unknown) => {
    if (busy) return;
    setBusy(true);
    const fresh = path.endsWith('/deal');
    const before = fresh ? { d: 0, p: 0 } : { d: table.dealerCards.length, p: table.playerCards.length };
    if (fresh || path.endsWith('/double')) play('chips');
    const r = await act(path, body);
    if (r) {
      setBase(before);
      if (fresh) setHand((h) => h + 1);
      setTable(r);
      // One "deal" sound per new card, a flip if the dealer's hidden card turns over, then the outcome.
      const steps: [number, string][] = [];
      let at = fresh ? 150 : 0;
      const newP = r.playerCards.length - before.p;
      const newD = r.dealerCards.length - before.d;
      const revealed = !fresh && table.dealerCards.includes('??') && !r.dealerCards.includes('??');
      if (revealed) { steps.push([at, 'flip']); at += 260; }
      for (let i = 0; i < Math.max(newP, newD) * 2; i++) {
        if ((i % 2 === 0 && i / 2 < newP) || (i % 2 === 1 && (i - 1) / 2 < newD)) { steps.push([at, 'deal']); at += 130; }
      }
      if (r.status === 'done' && r.outcome) {
        setShowOutcome(false);
        setTimeout(() => setShowOutcome(true), at + 200);
        const o = r.outcome;
        const sound = o === 'blackjack' ? 'bigWin' : o === 'win' ? 'win' : o === 'push' ? 'coin' : 'lose';
        steps.push([at + 250, sound]);
        setTimeout(() => {
          if (r.net > 0) { fx.float(`+${money(r.net)}`, 'cash'); if (o === 'blackjack') fx.coins(16); }
          else if (r.net < 0) fx.float(`−${money(-r.net)}`, 'loss');
        }, at + 250);
      }
      sequence(steps);
    }
    setBusy(false);
  };
  const betNum = Math.floor(Number(bet.replace(/[^0-9]/g, '')));
  const playing = table.status === 'playing';
  const chips = [100, 1000, 10_000, 100_000, 1_000_000].filter((c) => c <= table.maxBet);
  const chipColor: Record<number, string> = { 100: '#2a6ad8', 1000: '#222', 10_000: '#a01010', 100_000: '#6a2a8a', 1_000_000: '#b8860b' };

  return (
    <div>
      <p className="muted">
        Blackjack is played with cash on hand (<a onClick={() => go('bank')}>withdraw from the bank</a> if you need more). Dealer stands on all 17s.
        Blackjack pays 3 to 2. You can double down on your first two cards. Table limit at your level: <span className="money">{money(table.maxBet)}</span>.
      </p>
      <div className="felt">
        <div className="seat-label">Dealer {table.dealerTotal !== null && <span className="total-chip">{table.dealerTotal}</span>}</div>
        <div className="cards-row">{table.dealerCards.length ? table.dealerCards.map((c: string, i: number) => <Card key={`${hand}-${i}`} code={c} delay={Math.max(0, i - base.d) * 260 + 130} />) : <span className="dim">Place your bet</span>}</div>
        <div className="rules">BLACKJACK PAYS 3 TO 2 · DEALER STANDS ON 17</div>
        {table.status === 'done' && table.outcome && showOutcome && (
          <div className="outcome pop-in" style={{ color: OUTCOME[table.outcome][1] }}>
            {OUTCOME[table.outcome][0]}
            <div style={{ fontSize: 13 }}>{table.net > 0 ? <>You won {money(table.net)}</> : table.net < 0 ? <>You lost {money(-table.net)}</> : 'No money changes hands'}</div>
          </div>
        )}
        <div className="seat-label" style={{ marginTop: 8 }}>{me.name} {table.playerCards.length > 0 && <span className="total-chip">{table.playerTotal}</span>} {table.bet > 0 && <span className="small">· bet {money(table.bet)}</span>}</div>
        <div className="cards-row">{table.playerCards.map((c: string, i: number) => <Card key={`${hand}-${i}`} code={c} delay={Math.max(0, i - base.p) * 260} />)}</div>
      </div>

      <div style={{ marginTop: 10 }}>
        {playing ? (
          <div className="center">
            <button className="btn big" disabled={busy} onClick={() => send('/api/casino/hit')}>Hit</button>{' '}
            <button className="btn big" disabled={busy} onClick={() => send('/api/casino/stand')}>Stand</button>{' '}
            <button className={`btn big ${table.canDouble ? '' : 'disabled'}`} disabled={busy || !table.canDouble} onClick={() => send('/api/casino/double')} title="Double your bet, take exactly one more card">Double Down</button>
          </div>
        ) : (
          <Panel title={<>Place your bet · cash on hand <span className="money">{money(me.cash)}</span></>}>
            <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="chips">
                {chips.map((c) => (
                  <div key={c} className="chip" style={{ background: chipColor[c] }} onClick={() => setBet(String(Math.min(table.maxBet, (betNum || 0) + c)))} title={`Add ${money(c)}`}>
                    {c >= 1_000_000 ? '1M' : c >= 1000 ? `${c / 1000}K` : c}
                  </div>
                ))}
              </div>
              <div className="grow" />
              <div>
                $ <input value={bet} onChange={(e) => setBet(e.target.value)} style={{ width: 110, fontSize: 13 }} />{' '}
                <a className="small" onClick={() => setBet(String(table.minBet))}>min</a> · <a className="small" onClick={() => setBet(String(Math.min(table.maxBet, me.cash)))}>max</a>{' '}
                <button className={`btn big green ${betNum >= table.minBet && betNum <= me.cash ? '' : 'disabled'}`} disabled={busy} onClick={() => send('/api/casino/deal', { bet: betNum })}>Deal</button>
              </div>
            </div>
            <div className="small muted" style={{ marginTop: 6 }}>Minimum bet {money(table.minBet)}. The table limit rises by {money(content.rules.casinoMaxBetPerLevel ?? 25000)} per level.</div>
          </Panel>
        )}
      </div>
      <div className="small dim" style={{ marginTop: 6 }}>Hands played: {fmt(me.bjHands ?? 0)} · won: {fmt(me.bjWins ?? 0)}</div>
    </div>
  );
}

export function CasinoNav({ game }: { game: string }) {
  const tabs: [string, string, string][] = [
    ['blackjack', '#/casino/blackjack', '♠ Blackjack'],
    ['slots', '#/casino/slots', '7 Slots'],
    ['roulette', '#/casino/roulette', '● Roulette'],
    ['poker', '#/poker', '♣ Hold’em'],
    ['races', '#/races', '⚑ Horse Race'],
    ['sidebets', '#/sidebets', '⚖ Side Bets'],
  ];
  return (
    <>
      <h1 className="page-title">The Casino</h1>
      <div className="subnav casino-nav" style={{ marginBottom: 8 }}>
        {tabs.map(([id, href, label]) => <a key={id} className={game === id ? 'active' : ''} href={href}>{label}</a>)}
      </div>
    </>
  );
}

export function CasinoPage({ game = 'blackjack' }: { game?: string }) {
  const g = game === 'slots' || game === 'roulette' ? game : 'blackjack';
  return (
    <div>
      <CasinoNav game={g} />
      {g === 'slots' ? <SlotsMachine /> : g === 'roulette' ? <RouletteTable /> : <BlackjackTable />}
      <HighRollers />
    </div>
  );
}

/** This week's biggest casino winners. Last week's winner holds the High Roller title. */
function HighRollers() {
  const { me } = useGame();
  const { data } = useLoad(() => api.get('/api/casino/high-rollers'), [me?.cash]);
  if (!data) return null;
  return (
    <Panel title="High Rollers this week" right={<span className="small dim">resets Monday</span>}>
      {data.holder && (
        <div className="high-roller-holder">
          <Portrait seed={data.holder.portrait} playerId={data.holder.id} size={36} />
          <div><span className="gold">Current High Roller:</span> <PlayerLink id={data.holder.id} name={data.holder.name} /><div className="small dim">Last week’s biggest winner holds the title all this week.</div></div>
        </div>
      )}
      {data.rows.length === 0 ? <div className="muted">Nobody is ahead of the house this week. Yet.</div> : (
        <table className="list">
          <tbody>
            {data.rows.map((r: any, i: number) => (
              <tr key={r.id} style={r.id === me?.id ? { outline: '1px solid #b8860b' } : undefined}>
                <td className="gold" style={{ width: 24 }}><b>{i + 1}</b></td>
                <td><PlayerLink id={r.id} name={r.name} /></td>
                <td className="right money">+{money(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="small dim" style={{ marginTop: 4 }}>Whoever is on top when the week ends becomes the High Roller (and earns a secret achievement).</div>
    </Panel>
  );
}
